import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useSos } from "@/hooks/useSos";
import { useAuth } from "@/context/AuthContext";
import { Siren, PhoneCall, ShieldCheck, X, HeartHandshake } from "lucide-react";

export default function EmergencyButton() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const enabled = user?.role === "seeker";
  const { sos, limits, limitMsg, setLimitMsg, busy, holding, trigger, startHold, endHold, resolve } = useSos(enabled);

  if (!user || user.role !== "seeker" || location.pathname.startsWith("/family")) return null;

  const fire = () => trigger(note, () => { setOpen(false); setNote(""); });

  if (sos) {
    return (
      <div data-testid="sos-active-panel" className="fixed bottom-4 left-4 right-4 sm:left-auto sm:w-[420px] z-40 bg-white border-4 border-[#D96B43] rounded-3xl shadow-2xl p-5">
        <div className="flex items-start gap-3">
          <span className="w-12 h-12 rounded-2xl bg-[#FBEBE4] flex items-center justify-center shrink-0"><HeartHandshake className="w-7 h-7 text-[#D96B43]" /></span>
          <div className="flex-1">
            <p className="text-xl font-bold text-slate-900">Help is on the way</p>
            <p className="text-slate-700 mt-1">
              {sos.family === "sent" ? `We've alerted ${sos.contact_name || "your family contact"}. ` : "Add a family contact in Profile so we can alert them next time. "}
              {sos.helpers?.filter((h) => h.status === "sent").length > 0 && `${sos.helpers.filter((h) => h.status === "sent").length} nearby verified helper${sos.helpers.filter((h) => h.status === "sent").length === 1 ? "" : "s"} notified.`}
            </p>
            {sos.helper_name && <p data-testid="sos-helper-coming" className="mt-2 font-semibold text-[#1F5F4F] flex items-center gap-1"><ShieldCheck className="w-5 h-5" /> {sos.helper_name} is coming to you.</p>}
            <p className="mt-3 text-slate-800 font-semibold flex items-center gap-2"><PhoneCall className="w-5 h-5 text-[#D96B43]" /> In immediate danger? Call your local emergency number.</p>
          </div>
        </div>
        <button data-testid="sos-resolve-btn" onClick={resolve} disabled={busy}
          className="mt-4 w-full border-2 border-[#2E7D6B] text-[#1F5F4F] hover:bg-[#E8F4F1] disabled:opacity-60 font-bold min-h-[56px] rounded-xl text-lg transition-colors">
          Cancel — I'm okay
        </button>
      </div>
    );
  }

  return (
    <>
      <button data-testid="sos-button" onClick={() => setOpen(true)} aria-label="I need help now"
        className="fixed bottom-4 left-4 z-40 flex items-center gap-2 bg-[#C62828] hover:bg-[#B71C1C] text-white font-bold px-5 min-h-[60px] rounded-full shadow-2xl text-lg transition-transform hover:scale-105">
        <Siren className="w-7 h-7" /> I need help now
      </button>

      {open && (
        <div data-testid="sos-confirm" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border-4 border-[#C62828]">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-2xl font-display font-bold text-slate-900">Do you need help right now?</h2>
              <button data-testid="sos-close-btn" onClick={() => { setOpen(false); endHold(); setLimitMsg(""); }} className="p-2 min-w-[44px] min-h-[44px]"><X className="w-6 h-6" /></button>
            </div>
            <p className="text-slate-700 mt-2 text-lg">We'll alert your family contact and nearby verified helpers, and share your location.</p>
            {limits && (limits.cooldown_until && new Date(limits.cooldown_until) > Date.now() ? (
              <p data-testid="sos-cooldown-note" className="mt-3 bg-[#FFF7E6] text-[#A9741A] rounded-xl p-3 font-medium">You cancelled an alert a moment ago. You can send a new one in about {Math.max(1, Math.ceil((new Date(limits.cooldown_until) - Date.now()) / 60000))} min. If you need help now, please call your emergency number or family.</p>
            ) : limits.used_today >= limits.daily_cap ? (
              <p data-testid="sos-cap-note" className="mt-3 bg-[#FFF5F5] text-[#C62828] rounded-xl p-3 font-medium">You've reached today's limit of {limits.daily_cap} alerts. Please call your emergency number or your family directly.</p>
            ) : limits.used_today > 0 ? (
              <p data-testid="sos-used-note" className="mt-3 text-sm text-slate-500">{limits.used_today} of {limits.daily_cap} alerts used in the last 24 hours.</p>
            ) : null)}
            {limitMsg && <p data-testid="sos-limit-error" role="alert" className="mt-3 bg-[#FFF5F5] text-[#C62828] rounded-xl p-3 font-semibold">{limitMsg}</p>}
            <input data-testid="sos-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional: what's wrong? (e.g. I've fallen)"
              className="mt-4 w-full px-4 min-h-[52px] rounded-xl border-2 border-[#E2D9CE] focus:border-[#C62828] outline-none text-lg" />
            <button data-testid="sos-hold-btn" disabled={busy}
              onPointerDown={() => startHold(fire)} onPointerUp={endHold} onPointerLeave={endHold} onPointerCancel={endHold}
              className="relative mt-5 w-full overflow-hidden bg-[#C62828] hover:bg-[#B71C1C] disabled:opacity-60 text-white font-bold min-h-[72px] rounded-2xl text-xl select-none">
              <span className="absolute inset-y-0 left-0 bg-white/30 transition-[width] duration-100" style={{ width: `${holding}%` }} />
              <span className="relative">{busy ? "Sending alert…" : holding > 0 ? "Keep holding…" : "Hold for 3 seconds to send alert"}</span>
            </button>
            <button data-testid="sos-confirm-tap-btn" onClick={trigger} disabled={busy} className="mt-3 w-full text-[#C62828] font-semibold min-h-[48px] underline-offset-2 hover:underline">
              I can't hold — send now
            </button>
          </div>
        </div>
      )}
    </>
  );
}
